import { Search, X, ChevronDown, Check, GraduationCap } from 'lucide-react';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ApiService } from '../../services/api';

interface ScheduledTeacher {
    id: string;
    name: string;
    branch?: string;
    email?: string;
    avatar?: string;
}

interface ScheduledTeacherSelectorProps {
    value?: string | null;
    onChange: (teacherId: string, teacher?: ScheduledTeacher) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    allowClear?: boolean;
    source?: 'scheduled' | 'report';
}

export default function ScheduledTeacherSelector({
    value = null,
    onChange,
    placeholder = 'Öğretmen Seçin',
    className = '',
    disabled = false,
    allowClear = true,
    source = 'scheduled',
}: ScheduledTeacherSelectorProps) {
    const [teachers, setTeachers] = useState<ScheduledTeacher[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const fetchTeachers = async () => {
            try {
                setLoading(true);

                const response = source === 'report'
                    ? await ApiService.getAvailableTeachersForReport()
                    : await ApiService.getScheduledTeachers();
                const data = Array.isArray(response) ? response : ((response as any)?.data || []);

                setTeachers(
                    data.map((teacher: any) => ({
                        id: teacher.id,
                        name: teacher.name || teacher.fullName || `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
                        branch: teacher.branch,
                        email: teacher.email,
                        avatar: teacher.avatar,
                    }))
                );
            } catch (error) {
                console.error('Öğretmenler yüklenirken hata:', error);

                setTeachers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTeachers();
    }, [source]);

    const filteredTeachers = useMemo(() => {
        if (!searchTerm) return teachers;
        const lowerTerm = searchTerm.toLowerCase();

        return teachers.filter(t =>
            (t.name?.toLowerCase().includes(lowerTerm)) ||
            (t.branch?.toLowerCase().includes(lowerTerm))
        );
    }, [teachers, searchTerm]);

    const selectedTeacher = useMemo(() =>
        teachers.find((t) => t.id === value),
        [teachers, value]
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            };
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSelect = (teacherId: string) => {
        const teacher = teachers.find((t) => t.id === teacherId);

        onChange(teacherId, teacher);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = () => {
        if (allowClear) {
            onChange('', undefined);
            setIsOpen(false);
            setSearchTerm('');
        };
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
        } else if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
        };
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                className={`
          w-full px-4 py-2 text-left border rounded-lg focus:ring-2 focus:ring-blue-500
          transition-colors duration-200 flex items-center justify-between
          ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200" : "bg-white hover:border-gray-400 border-gray-300"}
          ${isOpen ? "ring-2 ring-blue-500 border-blue-500" : ""}
        `}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {selectedTeacher ? (
                        <>
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                                {selectedTeacher.avatar ? (
                                    <img
                                        src={selectedTeacher.avatar}
                                        alt={selectedTeacher.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-white font-medium text-[10px]">
                                        {selectedTeacher.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                    </span>
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <span className="truncate text-gray-900 text-sm font-medium">
                                    {selectedTeacher.name}
                                </span>
                                {selectedTeacher.branch && (
                                    <div className="text-xs text-gray-500 truncate">{selectedTeacher.branch}</div>
                                )}
                            </div>
                        </>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>

                <div className="flex items-center gap-1">
                    {selectedTeacher && allowClear && (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClear();
                            }}
                            className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                            role="button"
                        >
                            <X className="w-3 h-3 text-gray-400" />
                        </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                    <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Öğretmen ara..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-3 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                autoFocus
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto max-h-44">
                        {loading ? (
                            <div className="p-4 text-center text-gray-500">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                                <span className="text-xs mt-1 block">Yükleniyor...</span>
                            </div>
                        ) : filteredTeachers.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 text-sm">{searchTerm ? "Sonuç Bulunamadı" : "Öğretmen Bulunamadı"}</div>
                        ) : (
                            <>
                                {filteredTeachers.map((teacher) => {
                                    const isCurrentValue = value === teacher.id;

                                    return (
                                        <button
                                            key={teacher.id}
                                            type="button"
                                            onClick={() => handleSelect(teacher.id)}
                                            className={`
                        w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3
                        ${isCurrentValue ? "bg-blue-50 text-blue-600" : "text-gray-900"}
                      `}>
                                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                {teacher.avatar ? (
                                                    <img src={teacher.avatar} alt={teacher.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="text-white font-medium text-xs">
                                                        {teacher.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-gray-900 truncate">
                                                    {teacher.name}
                                                </div>
                                                {teacher.branch && (
                                                    <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                                                        <GraduationCap className="w-3 h-3" />
                                                        {teacher.branch}
                                                    </div>
                                                )}
                                            </div>
                                            {isCurrentValue && <Check className="w-4 h-4 ml-auto flex-shrink-0" />}
                                        </button>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
